<template>
  <v-navigation-drawer
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :rail="rail"
    permanent
    class="custom-drawer"
    color="surface"
    elevation="1"
  >
    <!-- Drawer Header -->
    <div class="drawer-header pa-4">
      <div class="d-flex align-center">
        <v-img
          src="/gpa_logo.png"
          alt="GPA Logo"
          max-width="40"
          class="mr-3"
        />
        <div v-show="!rail">
          <h3 class="text-h6 font-weight-bold text-primary">GPA Board</h3>
          <p class="text-caption text-medium-emphasis mb-0">
            Board Management System
          </p>
        </div>
      </div>
    </div>

    <v-divider />

    <!-- Navigation Items -->
    <v-list density="compact" nav>
      <v-list-item
        v-for="item in navigationItems"
        :key="item.title"
        :to="item.to"
        :prepend-icon="item.icon"
        rounded="xl"
        class="ma-2"
        color="primary"
        :exact="item.to === '/'"
      >
        <v-list-item-title>{{ item.title }}</v-list-item-title>
        <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
        <template #append v-if="item.badge">
          <v-badge
            :content="item.badge"
            color="error"
            inline
          />
        </template>
      </v-list-item>
    </v-list>

    <template #append>
      <div class="pa-2">
        <v-btn
          icon
          @click.stop="rail = !rail"
          size="small"
          variant="text"
        >
          <v-icon>
            {{ rail ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
          </v-icon>
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { ref, computed } from 'vue'
import { useAuthStore } from '@/store/auth'

export default {
  name: 'AppSidebar',
  props: {
    modelValue: {
      type: Boolean,
      default: true
    },
    pendingCount: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:modelValue'],
  setup(props) {
    const authStore = useAuthStore()
    const rail = ref(false)

    const user = computed(() => authStore.user || {})
    const isBoardMember = computed(() => authStore.isBoardMember)

    // Items visible to everyone (authenticated)
    const coreItems = [
      { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/', subtitle: 'Overview' },
      { title: 'My Profile',  icon: 'mdi-account-circle', to: '/profile', subtitle: 'Account & Settings' }
    ]

    // Items visible to board members + admins
    const boardItems = [
      { title: 'Meetings',      icon: 'mdi-calendar-account',         to: '/meetings',          subtitle: 'Schedule & Manage' },
      { title: 'Calendar',      icon: 'mdi-calendar',                 to: '/meetings/calendar', subtitle: 'Meeting Calendar' },
      { title: 'Documents',     icon: 'mdi-file-document-multiple',   to: '/documents',         subtitle: 'File Management' },
      { title: 'Resolutions',   icon: 'mdi-gavel',                    to: '/resolutions',       subtitle: 'Voting & Decisions' },
      { title: 'Board Members', icon: 'mdi-account-group',            to: '/members',           subtitle: 'Member Directory' },
      { title: 'Committees',    icon: 'mdi-account-group-outline',    to: '/committees',        subtitle: 'Board Committees' }
    ]

    // Items visible to admins only
    const adminItems = [
      { title: 'User Management', icon: 'mdi-account-cog',  to: '/users',    subtitle: 'Manage Users' },
      { title: 'Reports',         icon: 'mdi-chart-line',   to: '/reports',  subtitle: 'Analytics' },
      { title: 'Settings',        icon: 'mdi-cog',          to: '/settings', subtitle: 'System Configuration' }
    ]

    const navigationItems = computed(() => {
      const u = user.value
      if (u.is_admin) return [...coreItems, ...boardItems, ...adminItems]
      if (isBoardMember.value) return [...coreItems, ...boardItems]
      return coreItems // plain users: Dashboard + Profile only
    })

    return {
      rail,
      user,
      navigationItems
    }
  }
}
</script>

<style scoped>
.custom-drawer {
  position: fixed !important;
  height: 100vh !important;
  top: 0 !important;
  left: 0 !important;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.drawer-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-list-item--active {
  background: rgba(var(--v-theme-primary), 0.1) !important;
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
